import { Vector2 } from 'three';

import { LineSegment2D, Point2D } from '@paragon/geometry-library';
import { newVector2, Point2D as Point2DInterface } from '@paragon/three-shared';
import { assertExhaustiveSwitch, max, sortBy } from '@paragon/utilities';

import { LoadVisualScaler } from '../../store/selectors/getActiveComponentDesignLoadVisualScaler';
import { DrawableLoad } from '../loadProjectionUtilities';

const areVectorsPointingInRelativeSameDirection = (vectorA: Vector2, vectorB: Vector2, tolerance: number) => {
  const normalizedVectorA = newVector2(vectorA).normalize();
  const normalizedVectorB = newVector2(vectorB).normalize();
  const dot = normalizedVectorA.dot(normalizedVectorB);
  return dot >= 1 - tolerance;
};

const areLoadsStackAble = (drawableLoad: DrawableLoad, compareDrawableLoad: DrawableLoad) => {
  if (drawableLoad.draw !== compareDrawableLoad.draw) return false;
  const { load } = drawableLoad;
  const { load: compareLoad } = compareDrawableLoad;
  const vectorA = newVector2(load.direction);
  const vectorB = newVector2(compareLoad.direction);
  if (!areVectorsPointingInRelativeSameDirection(vectorA, vectorB, 0.01)) return false;
  const loadStart = Point2D.fromObject(load.start);
  const loadEnd = load.end && Point2D.fromObject(load.end);
  const loadSegment = loadEnd && !loadEnd.equals(loadStart) && new LineSegment2D(loadStart, loadEnd);
  const compareLoadStart = Point2D.fromObject(compareLoad.start);
  const compareLoadEnd = compareLoad.end && Point2D.fromObject(compareLoad.end);
  const compareSegment =
    compareLoadEnd && !compareLoadEnd.equals(compareLoadStart) && new LineSegment2D(compareLoadStart, compareLoadEnd);
  if (load.distribution === 'Distributed' && compareLoad.distribution === 'Distributed')
    return loadSegment && compareSegment && loadSegment.overlaps(compareSegment);
  if (load.distribution === 'Distributed') return loadSegment && loadSegment.contains(compareLoadStart);
  if (compareLoad.distribution === 'Distributed') return compareSegment && compareSegment.contains(loadStart);
  return loadStart.equals(compareLoadStart);
};

type Offsets = { [guid: string]: Point2DInterface };
const solveStackingLoads = (
  loads: ReadonlyArray<DrawableLoad>,
  scaler: LoadVisualScaler,
  highestLoadY: number,
): Offsets => {
  if (loads.length === 0) return {};
  const sortedLoads = sortBy(loads, ({ load }) => (load.distribution === 'Concentrated' ? -1 : 1), 'desc');
  // stacking strategy = use largest magnitude converted to distance on load *
  // reverse direction and add it to previous overlapping load
  const loadsWithOffsets: { drawableLoad: DrawableLoad; offset: Point2DInterface }[] = [];
  const offsetDistances: { [guid: string]: number } = {};
  for (const { drawableLoad, index } of sortedLoads.map((drawableLoad, index) => ({ drawableLoad, index }))) {
    const maxOffset =
      max(
        sortedLoads
          .filter((load, i) => i < index && areLoadsStackAble(drawableLoad, load))
          .map(({ load }) => offsetDistances[load.guid]),
      ) ?? 0;
    const { load } = drawableLoad;
    const stackLocation = newVector2(load.direction).negate().multiplyScalar(maxOffset);
    loadsWithOffsets.push({ drawableLoad, offset: stackLocation });
    const currentLoadMaxOffset = Math.max(
      scaler(load.startMagnitude, load.distribution),
      scaler(load.endMagnitude ?? 0, load.distribution),
    );
    offsetDistances[load.guid] = maxOffset + currentLoadMaxOffset;
  }
  // Now shift unprojected loads to their final resting place
  const bottomLoadShift =
    max(
      loadsWithOffsets
        .filter(({ drawableLoad }) => drawableLoad.draw === 'Bottom')
        .map(({ drawableLoad }) => offsetDistances[drawableLoad.load.guid]),
    )! + 6;
  return Object.fromEntries(
    loadsWithOffsets.map(({ drawableLoad: { draw, load }, offset }) => {
      if (draw === 'Projected') return [load.guid, offset];
      if (draw === 'Bottom') return [load.guid, { ...offset, y: offset.y - bottomLoadShift }];
      if (draw === 'Top') return [load.guid, { ...offset, y: offset.y + highestLoadY }];
      return assertExhaustiveSwitch(draw);
    }),
  );
};

export default solveStackingLoads;
